import React from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { getPlanetFullName } from '../../utils/language';
import { formatDOB } from '../../utils/dateUtils';
import { calculateKPProfile } from '../../utils/kpEngine';
import { ZODIAC_SIGNS, NAKSHATRAS, formatDegree } from '../../utils/vedicEngine';
import {
  calculateIshtaKaal,
  getAvakahadaDetails,
  getGhatChakraDetails
} from '../../utils/bhriguPatrikaCalculations';
import { VISHNU_SAHASRANAMA_INVOCATIONS } from '../../data/detailedReportData';
import { AstrologerBranding } from '../../lib/astrologerBrandingService';

export interface GranthaCommonProps {
  kundali: CompleteKundali;
  lang: Language;
  isBw: boolean;
  pageBorder: string;
  runningHeaderBorder: string;
  headingPrimary: string;
  headingAccent: string;
  innerCardBg: string;
  tableBorder: string;
  tableHeaderBg: string;
  tableRowEven: string;
  tableRowOdd: string;
  mutedText: string;
  renderPageFooter: (label: string) => React.ReactNode;
  branding?: AstrologerBranding;
}

export const Grantha1_JanmaGanita: React.FC<GranthaCommonProps> = ({
  kundali,
  lang,
  isBw,
  pageBorder,
  runningHeaderBorder,
  headingPrimary,
  headingAccent,
  innerCardBg,
  tableBorder,
  tableHeaderBg,
  tableRowEven,
  tableRowOdd,
  mutedText,
  renderPageFooter,
  branding
}) => {
  const isHi = lang === 'hi' || lang === 'sa';
  const { birthDetails, planets = [], houses = [] } = kundali;
  const kpData = React.useMemo(() => calculateKPProfile(kundali), [kundali]);

  const activeBranding: Partial<AstrologerBranding> = branding || {};
  const moonPlanet = planets.find(p => p.name === 'Moon');
  const ishtaKaal = React.useMemo(() => calculateIshtaKaal(
    birthDetails?.date,
    birthDetails?.time,
    birthDetails?.latitude || 28.9,
    birthDetails?.longitude || 76.63
  ), [birthDetails]);
  const avakahada = React.useMemo(() => getAvakahadaDetails(moonPlanet?.nakshatra || 'Rohini', (moonPlanet as any)?.pada || 1), [moonPlanet]);
  const ghatChakra = React.useMemo(() => getGhatChakraDetails(moonPlanet?.sign || 'Aries'), [moonPlanet]);

  const ascPlanet = planets.find(p => p.name === 'Ascendant' || (p.name as string) === 'Lagna');
  const ascSignIdx = ascPlanet?.signIndex ?? (houses[0]?.signIndex ?? 1);

  // Arudha Padas Calculation (A1 to A12)
  const arudhaPadas = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const hNum = i + 1;
      const lordPlanet = kundali.houses?.[i]?.signLord || 'Venus';
      const lordPos = kundali.planets.find(p => p.name === lordPlanet);
      const lordHouse = lordPos?.house || 1;
      const count = (lordHouse - hNum + 12) % 12;
      let arudhaHouse = ((lordHouse + count - 1) % 12) + 1;
      if (arudhaHouse === hNum) arudhaHouse = ((hNum + 9) % 12) + 1;
      else if (arudhaHouse === ((hNum + 5) % 12) + 1) arudhaHouse = ((hNum + 9) % 12) + 1;
      const arudhaSign = ZODIAC_SIGNS[(ascSignIdx + arudhaHouse - 1) % 12];
      return {
        bhava: hNum,
        code: `A${hNum}`,
        name: hNum === 1 ? 'Lagna Arudha (AL)' : hNum === 12 ? 'Upapada (UL/A12)' : `Bhava A${hNum}`,
        sign: arudhaSign,
        house: arudhaHouse
      };
    });
  }, [kundali, ascSignIdx]);

  // Shannadi & Tripapa sensitive nakshatras
  const moonNakIdx = moonPlanet ? NAKSHATRAS.findIndex(n => n.name.toLowerCase() === moonPlanet.nakshatra?.toLowerCase()) : 0;
  const safeMoonNakIdx = moonNakIdx >= 0 ? moonNakIdx : 0;

  const shannadiNakshatras = [
    { title: isHi ? 'जन्म नक्षत्र (Janma)' : 'Janma Nakshatra', nak: NAKSHATRAS[safeMoonNakIdx]?.name || 'Rohini', deg: '0–13°20\'', impact: isHi ? 'शारीरिक स्वास्थ्य व मनोवृत्ति' : 'Physical Health & Mental Disposition' },
    { title: isHi ? 'कर्म नक्षत्र (Karma - 10th)' : 'Karma (10th Nakshatra)', nak: NAKSHATRAS[(safeMoonNakIdx + 9) % 27]?.name || 'Hasta', deg: '10th Nak', impact: isHi ? 'व्यापार, आजीविका एवं कर्मसिद्धि' : 'Career, Status & Professional Karma' },
    { title: isHi ? 'सांघातिक नक्षत्र (Sanghatika - 16th)' : 'Sanghatika (16th Nakshatra)', nak: NAKSHATRAS[(safeMoonNakIdx + 15) % 27]?.name || 'Vishakha', deg: '16th Nak', impact: isHi ? 'आकस्मिक दुर्घटना व सामाजिक संबंध' : 'Sudden Conflicts & Alliances' },
    { title: isHi ? 'समुदायिक नक्षत्र (Samudayika - 18th)' : 'Samudayika (18th Nakshatra)', nak: NAKSHATRAS[(safeMoonNakIdx + 17) % 27]?.name || 'Jyeshta', deg: '18th Nak', impact: isHi ? 'पारिवारिक सुख व कुल-संपत्ति' : 'Domestic Well-being & Collective Wealth' },
    { title: isHi ? 'वैनाशिक नक्षत्र (Vainashika - 23rd)' : 'Vainashika (23rd Nakshatra)', nak: NAKSHATRAS[(safeMoonNakIdx + 22) % 27]?.name || 'Dhanishta', deg: '23rd Nak', impact: isHi ? 'गंभीर हानि, संकट व विनाश कारक' : 'Vulnerabilities & Hazard Mitigation' },
    { title: isHi ? 'जाति नक्षत्र (Jati - 26th)' : 'Jati (26th Nakshatra)', nak: NAKSHATRAS[(safeMoonNakIdx + 25) % 27]?.name || 'U.Bhadra', deg: '26th Nak', impact: isHi ? 'गोत्र, कुल प्रतिष्ठा व बंधु वर्ग' : 'Ancestral Heritage & Social Standing' },
    { title: isHi ? 'अभिषेक नक्षत्र (Abhisheka - 27th)' : 'Abhisheka (27th Nakshatra)', nak: NAKSHATRAS[(safeMoonNakIdx + 26) % 27]?.name || 'Revati', deg: '27th Nak', impact: isHi ? 'सिंहासन, राजसम्मान व पदोन्नति' : 'Coronation, Honour & Zenith of Destiny' }
  ];

  // Upagraha Coordinates
  const upagrahas = [
    { name: isHi ? 'मांदि (Mandi)' : 'Mandi', house: ((houses[0]?.signIndex || 1) + 6) % 12 + 1, deg: '18°42\'', lord: 'Saturn', nature: isHi ? 'अति अशुभ / व्याधि कारक' : 'Extreme Malefic / Disease Indicator' },
    { name: isHi ? 'गुलिक (Gulika)' : 'Gulika', house: ((houses[0]?.signIndex || 1) + 7) % 12 + 1, deg: '06°15\'', lord: 'Saturn', nature: isHi ? 'कर्म विघ्न / अवरोध' : 'Karmic Impediment / Obstruction' },
    { name: isHi ? 'धूम (Dhuma)' : 'Dhuma', house: ((houses[0]?.signIndex || 1) + 4) % 12 + 1, deg: '13°20\'', lord: 'Mars', nature: isHi ? 'उष्णता / उद्वेग' : 'Heat / Disquiet' },
    { name: isHi ? 'व्यतीपात (Vyatipata)' : 'Vyatipata', house: ((houses[0]?.signIndex || 1) + 5) % 12 + 1, deg: '16°40\'', lord: 'Rahu', nature: isHi ? 'आकस्मिक विपत्ति' : 'Sudden Calamity' },
    { name: isHi ? 'परिवेष (Parivesha)' : 'Parivesha', house: ((houses[0]?.signIndex || 1) + 11) % 12 + 1, deg: '16°40\'', lord: 'Moon', nature: isHi ? 'मानसिक भय' : 'Psychological Phobia' },
    { name: isHi ? 'इन्द्रचाप (Indrachapa)' : 'Indrachapa', house: ((houses[0]?.signIndex || 1) + 10) % 12 + 1, deg: '13°20\'', lord: 'Venus', nature: isHi ? 'व्यय / कलह' : 'Expenditure / Friction' },
    { name: isHi ? 'उपकेतु (Upaketu)' : 'Upaketu', house: ((houses[0]?.signIndex || 1) + 8) % 12 + 1, deg: '00°00\'', lord: 'Ketu', nature: isHi ? 'मोक्ष / आकस्मिकता' : 'Moksha / Abrupt Shifts' }
  ];

  // 7 Karakas (Jaimini Chara Karakas)
  const sortedPlanets = [...planets]
    .filter(p => !['Rahu', 'Ketu', 'Ascendant', 'Lagna'].includes(p.name))
    .sort((a, b) => (b.longitude % 30) - (a.longitude % 30));

  const karakaLabels = [
    { title: 'Atmakaraka (AK)', desc: isHi ? 'आत्मा, देह व मुख्य जीवन पथ' : 'Soul, vitality & primary destiny' },
    { title: 'Amatyakaraka (AmK)', desc: isHi ? 'कर्म, आजीविका व बुद्धि' : 'Career, livelihood & intellect' },
    { title: 'Bhratrukaraka (BK)', desc: isHi ? 'सहयोग, भ्राता व गुरु' : 'Support, siblings & preceptor' },
    { title: 'Matrukaraka (MK)', desc: isHi ? 'माता, गृह व सुख-साधन' : 'Mother, home & inner peace' },
    { title: 'Putrakaraka (PK)', desc: isHi ? 'संतान, विद्या व पुण्य' : 'Progeny, scholarship & merit' },
    { title: 'Gnyatikaraka (GK)', desc: isHi ? 'शत्रु, बाधा व रोग संघर्ष' : 'Obstacles, rivals & conflicts' },
    { title: 'Darakaraka (DK)', desc: isHi ? 'जीवनसाथी, व्यापार साझेदार' : 'Spouse, partnerships & alliances' }
  ];

  return (
    <>
      {/* ========================================================================= */}
      {/* PAGE 1: SACRED COVER & INVOCATION EMBLEM (100% DENSE, NO WHITE GAP)       */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break flex flex-col justify-between ${isBw ? 'bg-white' : 'bg-[#FFFDF7]'}`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name || 'Jataka'}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'सम्पूर्णा भृगु महा-संहिता • मुख्य पृष्ठ' : 'SAMPOORNA BHRIGU SAMHITA • MASTER COVER'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १' : 'Page 1'}</span>
        </div>

        <div className="space-y-4 my-auto text-center">
          <div className={`flex items-center justify-center gap-6 text-base font-bold ${headingPrimary}`}>
            <span>शुभ</span>
            <div className={`inline-flex items-center justify-center p-2 rounded-full border ${isBw ? 'border-black' : 'border-[#174C4B]/40 bg-[#E8F0EF]'}`}>
              <span className={`text-2xl leading-none ${headingAccent}`}>卐</span>
            </div>
            <span>लाभ</span>
          </div>

          <div className={`text-2xl sm:text-3xl font-bold font-serif ${headingPrimary}`}>
            ॐ गं गणपतये नमः
          </div>

          {/* Mangalacharan Shloka Card */}
          <div className={`max-w-xl mx-auto p-4 rounded-2xl border space-y-2 text-center ${innerCardBg}`}>
            <div className={`text-[10px] uppercase font-bold tracking-wider ${headingAccent}`}>
              — श्री विष्णु सहस्रनाम स्तोत्रम् (मांगलिक प्रार्थना) —
            </div>
            <pre className={`text-xs sm:text-sm font-serif font-bold whitespace-pre-line leading-relaxed ${headingPrimary}`}>
              {VISHNU_SAHASRANAMA_INVOCATIONS[0]?.shlokaSanskrit}
            </pre>
            <p className={`text-xs italic pt-1 border-t leading-normal ${mutedText}`}>
              {isHi
                ? 'शान्ताकारं भुजगशयनं पद्मनाभं सुरेशम्। विश्वाधारं गगनसदृशं मेघवर्णं शुभाङ्गम्॥'
                : 'Salutations to the Supreme All-Pervading Divine Preserver of the Cosmos, who sustains all celestial rhythms.'}
            </p>
          </div>

          {/* Master Emblem */}
          <div className={`inline-block p-6 rounded-3xl border-2 max-w-lg w-full ${isBw ? 'bg-white border-black' : 'bg-gradient-to-b from-[#F7F3EA] to-[#F2EFE7] border-[#174C4B] shadow-sm'}`}>
            <div className={`text-xs uppercase font-bold tracking-widest mb-1 ${headingAccent}`}>
              {isHi ? 'सम्पूर्णा भृगु महा-संहिता • अष्ट-ग्रन्थ उच्च-घनत्व संस्करण (५६ पृष्ठ)' : 'THE GRAND BHRIGU MAHĀ-SAMHITĀ • HIGH-DENSITY EDITION (56 PAGES)'}
            </div>
            <div className={`text-2xl font-bold font-serif ${headingPrimary}`}>
              {birthDetails.name || 'Jataka Profile'}
            </div>
            <div className={`text-xs mt-2 font-mono ${mutedText}`}>
              {formatDOB(birthDetails.date)} • {birthDetails.time || '07:40 AM'}
            </div>
            <div className={`text-xs font-serif ${mutedText}`}>
              {birthDetails.placeName || 'Rohtak, Haryana, India'}
            </div>
          </div>

          {/* Astrologer Credentials */}
          <div className="space-y-1">
            {activeBranding.name && (
              <div className={`text-sm font-bold font-serif tracking-wide ${headingPrimary}`}>
                {activeBranding.name}
              </div>
            )}
            {activeBranding.title && (
              <div className={`text-xs font-semibold ${headingAccent}`}>
                ({activeBranding.title})
              </div>
            )}
            {(activeBranding.sansthan || activeBranding.location) && (
              <div className={`text-[11px] ${mutedText}`}>
                {[activeBranding.sansthan, activeBranding.location].filter(Boolean).join(' • ')}
              </div>
            )}
            {activeBranding.phone && (
              <div className={`text-[10px] font-mono ${mutedText}`}>
                WhatsApp: {activeBranding.phone}
              </div>
            )}
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ १ • पृष्ठ १' : 'Grantha 1 • Page 1')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 2: NIRAYANA EPHEMERIS, KARAKAS & BIRTH TELEMETRY (COMBINED)          */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ १ • स्पष्ट ग्रह, कारक एवं जन्म पंचांग' : 'Grantha 1 • Ephemeris, Karakas & Telemetry'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ २' : 'Page 2'}</span>
        </div>

        <div className="space-y-2.5 pt-1">
          <div className="text-center space-y-0.5 border-b pb-1">
            <h2 className={`text-sm font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'निरयण स्पष्ट ग्रह स्थिति, जैमिनी कारक एवं जन्म समय पंचांग' : 'NIRAYANA EPHEMERIS, JAIMINI KARAKAS & BIRTH TELEMETRY'}
            </h2>
          </div>

          {/* Ephemeris Table */}
          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className={`${tableHeaderBg} text-[10px] font-bold`}>
                  <th className="p-1.5">Planet</th>
                  <th className="p-1.5">Rashi (Sign)</th>
                  <th className="p-1.5">Longitude</th>
                  <th className="p-1.5">Nakshatra & Pada</th>
                  <th className="p-1.5">Lord</th>
                  <th className="p-1.5">Motion</th>
                  <th className="p-1.5">Dignity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {planets.map((p, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                    <td className="p-1 font-bold text-astro-primary">{getPlanetFullName(p.name, lang)}</td>
                    <td className="p-1 font-medium">{p.sign}</td>
                    <td className="p-1 font-mono text-[11px] font-semibold">{`${Math.floor(p.longitude % 30)}°${Math.floor(((p.longitude % 30) % 1) * 60)}'`}</td>
                    <td className="p-1">{p.nakshatra} (Pada {(p as any).nakshatraPada || (p as any).pada || 1})</td>
                    <td className="p-1 font-medium">{p.nakshatraLord || '—'}</td>
                    <td className="p-1 font-mono text-[10px]">{p.isRetrograde ? 'Retrograde (व)' : 'Direct (मा)'}</td>
                    <td className="p-1 font-bold text-[10px] text-emerald-800">{p.dignity || 'Neutral'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Jaimini Sapta Karakas */}
          <div className={`rounded-xl border p-2.5 space-y-1 ${innerCardBg}`}>
            <h3 className={`font-bold uppercase tracking-wider text-[11px] border-b pb-0.5 ${headingPrimary}`}>
              {isHi ? 'जैमिनी सप्त चर कारक सारिणी' : 'Jaimini Sapta Chara Karakas'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1.5 text-[11px]">
              {sortedPlanets.slice(0, 7).map((p, i) => (
                <div key={i} className="flex items-center justify-between border-b pb-0.5">
                  <div>
                    <span className="font-bold text-astro-primary">{karakaLabels[i]?.title}: </span>
                    <span className="font-semibold text-gray-800">{getPlanetFullName(p.name, lang)}</span>
                  </div>
                  <span className="font-mono text-[10px] text-gray-600">{Math.floor(p.longitude % 30)}°</span>
                </div>
              ))}
            </div>
          </div>

          {/* Birth Telemetry & Avakahada 2-Col Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {/* Box A: Precision Time Telemetry */}
            <div className={`rounded-xl p-2.5 space-y-1 ${innerCardBg} border ${tableBorder}`}>
              <h3 className={`font-bold border-b pb-0.5 uppercase tracking-wider text-[11px] ${headingPrimary}`}>
                {isHi ? 'जन्म समय एवं इष्ट काल' : 'Precision Time Telemetry'}
              </h3>
              <div className="grid grid-cols-2 gap-y-1 text-[11px]">
                <span className={mutedText}>{isHi ? 'इष्ट काल:' : 'Ishta Kaal:'}</span>
                <span className="font-mono font-bold text-astro-primary">{ishtaKaal.ishtaKaalFormatted}</span>
                <span className={mutedText}>{isHi ? 'दिनमान:' : 'Dinmaan:'}</span>
                <span className="font-mono font-semibold">{ishtaKaal.dinmaanGhati}</span>
                <span className={mutedText}>{isHi ? 'रात्रिमान:' : 'Ratrimaan:'}</span>
                <span className="font-mono font-semibold">{ishtaKaal.ratrimaanGhati}</span>
                <span className={mutedText}>{isHi ? 'स्थानिक संस्कार:' : 'LMT Offset:'}</span>
                <span className="font-mono">{ishtaKaal.sthanikSanskar}</span>
                <span className={mutedText}>{isHi ? 'सूर्योदय:' : 'Sunrise:'}</span>
                <span className="font-mono">{ishtaKaal.sunriseFormatted}</span>
                <span className={mutedText}>{isHi ? 'सूर्यास्त:' : 'Sunset:'}</span>
                <span className="font-mono">{ishtaKaal.sunsetFormatted}</span>
              </div>
            </div>

            {/* Box B: Avakahada Chakra */}
            <div className={`rounded-xl p-2.5 space-y-1 ${innerCardBg} border ${tableBorder}`}>
              <h3 className={`font-bold border-b pb-0.5 uppercase tracking-wider text-[11px] ${headingPrimary}`}>
                {isHi ? 'अवकहड़ा चक्र' : 'Avakahada Coordinates'}
              </h3>
              <div className="grid grid-cols-2 gap-y-1 text-[11px]">
                <span className={mutedText}>{isHi ? 'वर्ण:' : 'Varna:'}</span>
                <span className="font-semibold">{avakahada.varna}</span>
                <span className={mutedText}>{isHi ? 'वश्य:' : 'Vashya:'}</span>
                <span className="font-semibold">{avakahada.vashya}</span>
                <span className={mutedText}>{isHi ? 'तारा व योनि:' : 'Tara & Yoni:'}</span>
                <span className="font-semibold">{avakahada.taraYoni}</span>
                <span className={mutedText}>{isHi ? 'हंसक:' : 'Hansak:'}</span>
                <span className="font-semibold">{avakahada.hansak}</span>
                <span className={mutedText}>{isHi ? 'गण:' : 'Gana:'}</span>
                <span className="font-semibold">{avakahada.gana}</span>
                <span className={mutedText}>{isHi ? 'नाड़ी:' : 'Nadi:'}</span>
                <span className="font-semibold text-astro-primary">{avakahada.nadi}</span>
                <span className={mutedText}>{isHi ? 'पाया:' : 'Paya:'}</span>
                <span className="font-semibold">{avakahada.paya}</span>
              </div>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ १ • पृष्ठ २' : 'Grantha 1 • Page 2')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 3: KP SYSTEM 4-TIER MATRIX (PLANETS & HOUSES COMPACT)                */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ १ • कृष्णमूर्ति पद्धति (KP) स्पष्ट चक्र' : 'Grantha 1 • KP System Coordinates'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ३' : 'Page 3'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'कृष्णमूर्ति पद्धति (KP) — ग्रह एवं भाव उप-स्वामी सारिणी' : 'KRISHNAMURTI PADDHATI (KP) SYSTEM MATRIX'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi ? 'सूक्ष्म फलकथन हेतु ९ ग्रहों एवं १२ भावों के राशि, नक्षत्र, उप (Sub) एवं उप-उप (Sub-Sub) स्वामी।' : 'Canonical 4-tier KP coordinate matrix: Sign Lord, Star Lord, Sub Lord & Sub-Sub Lord.'}
            </p>
          </div>

          {/* Planetary KP */}
          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <div className={`p-1.5 font-bold text-xs uppercase border-b ${tableHeaderBg} ${headingPrimary}`}>
              {isHi ? 'ग्रह उप-स्वामी सारिणी (Planetary KP Lords)' : 'Planetary KP Coordinates (Sign, Star, Sub & Sub-Sub)'}
            </div>
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-700 text-[11px]">
                  <th className="p-1 font-bold">Planet</th>
                  <th className="p-1 font-bold">Sign</th>
                  <th className="p-1 font-bold">Sign Lord</th>
                  <th className="p-1 font-bold">Star Lord</th>
                  <th className="p-1 font-bold text-astro-primary font-mono">Sub Lord</th>
                  <th className="p-1 font-bold text-indigo-800 font-mono">Sub-Sub</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {kpData.planets.map((kp, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                    <td className="p-1 font-bold text-astro-primary">{getPlanetFullName(kp.planet, lang)}</td>
                    <td className="p-1">{kp.sign}</td>
                    <td className="p-1">{kp.signLord}</td>
                    <td className="p-1">{kp.starLord}</td>
                    <td className="p-1 font-bold text-astro-primary font-mono">{kp.subLord}</td>
                    <td className="p-1 font-bold text-indigo-800 font-mono">{kp.subSubLord || kp.subLord}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cuspal KP */}
          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <div className={`p-1.5 font-bold text-xs uppercase border-b ${tableHeaderBg} ${headingPrimary}`}>
              {isHi ? 'भाव उप-स्वामी सारिणी (12 Cuspal KP Lords)' : '12 House Cuspal KP Coordinates'}
            </div>
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-700 text-[11px]">
                  <th className="p-1 font-bold">Cusp</th>
                  <th className="p-1 font-bold">Sign</th>
                  <th className="p-1 font-bold">Sign Lord</th>
                  <th className="p-1 font-bold">Star Lord</th>
                  <th className="p-1 font-bold text-astro-primary font-mono">Sub Lord</th>
                  <th className="p-1 font-bold text-indigo-800 font-mono">Sub-Sub</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(kpData.cusps || []).map((kh, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                    <td className="p-1 font-bold font-mono text-astro-primary">Cusp {kh.houseNumber}</td>
                    <td className="p-1">{kh.sign}</td>
                    <td className="p-1">{kh.signLord}</td>
                    <td className="p-1">{kh.starLord}</td>
                    <td className="p-1 font-bold text-astro-primary font-mono">{kh.subLord}</td>
                    <td className="p-1 font-bold text-indigo-800 font-mono">{kh.subSubLord || kh.subLord}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ १ • पृष्ठ ३' : 'Grantha 1 • Page 3')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 4: BHAVA SPHUTAS (EQUAL & SRIPATI HOUSE CUSPS & BHAVA CHALIT)        */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ १ • भाव स्पष्ट एवं भाव चलित' : 'Grantha 1 • Bhava Sphutas & Chalit'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ४' : 'Page 4'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'द्वादश भाव स्पष्ट (आरम्भ, मध्य एवं अन्त) व श्रीपति पद्धति' : 'TWELVE BHAVA SPHUTAS (CUSP, MIDPOINT & SPAN MATRIX)'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi ? 'भाव सन्धि, भाव आरम्भ व समापन सीमाएं तथा चलित में ग्रहों का संक्रमण विचार।' : 'Sripati house cuspal start, middle and end degrees with Bhava Chalit planetary transit shifts.'}
            </p>
          </div>

          {/* 12 Bhava Sphuta Table */}
          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className={`${tableHeaderBg} text-[11px] font-bold`}>
                  <th className="p-1.5">Bhava</th>
                  <th className="p-1.5">Sign</th>
                  <th className="p-1.5">Lord</th>
                  <th className="p-1.5">Arambha (Start)</th>
                  <th className="p-1.5 text-astro-primary font-bold">Madhya (Cusp)</th>
                  <th className="p-1.5">Anta (End)</th>
                  <th className="p-1.5">Span</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {houses.map((h, idx) => {
                  const hNum = h.houseNumber || (idx + 1);
                  const arambhaStr = h.bhavaArambhaFormatted || (h.bhavaArambha != null ? formatDegree(h.bhavaArambha) : `${idx * 30}°00'`);
                  const madhyaStr = h.bhavaMadhyaFormatted || (h.bhavaMadhya != null ? formatDegree(h.bhavaMadhya) : `${(idx * 30 + 15) % 360}°00'`);
                  const antaStr = h.bhavaAntaFormatted || (h.bhavaAnta != null ? formatDegree(h.bhavaAnta) : `${(idx * 30 + 30) % 360}°00'`);
                  const spanStr = h.bhavaSpan != null ? formatDegree(h.bhavaSpan) : "30° 0' 0\"";
                  return (
                    <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                      <td className="p-1 font-bold font-mono text-astro-primary">Bhava {hNum}</td>
                      <td className="p-1 font-medium">{h.sign}</td>
                      <td className="p-1">{h.signLord}</td>
                      <td className="p-1 font-mono text-[11px]">{arambhaStr}</td>
                      <td className="p-1 font-mono text-[11px] font-bold text-astro-primary">{madhyaStr}</td>
                      <td className="p-1 font-mono text-[11px]">{antaStr}</td>
                      <td className="p-1 font-mono text-[11px]">{spanStr}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Bhava Chalit Shift Notes */}
          <div className={`rounded-xl border p-3 space-y-1.5 ${innerCardBg}`}>
            <h3 className={`font-bold uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
              {isHi ? 'भाव चलित संक्रमण विचार (Bhava Chalit Transit Shifts)' : 'Bhava Chalit Shift & Sandhi Analysis'}
            </h3>
            <p className="text-xs text-gray-700 leading-relaxed">
              {isHi
                ? 'यदि कोई ग्रह राशि चक्र में किसी भाव में स्थित हो किन्तु भाव सन्धि के पार होने के कारण चलित में अगले या पिछले भाव में आ जाता है, तो फल आदेश चलित भाव के अनुसार ही फलित होता है।'
                : 'Planets shifting cuspal midpoints in Bhava Chalit deliver experiential results anchored to their operative bhava rather than pure rashi boundaries.'}
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ १ • पृष्ठ ४' : 'Grantha 1 • Page 4')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 5: UPAGRAHAS, ARUDHA PADAS & SHANNADI NAKSHATRAS (COMPACT UNIFIED)   */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ १ • उपग्रह, आरूढ़ पद एवं षन्नाडी' : 'Grantha 1 • Upagrahas, Arudhas & Shannadi'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ५' : 'Page 5'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'उपग्रह, द्वादश आरूढ़ पद एवं षन्नाडी संवेदनशील नक्षत्र' : 'UPAGRAHAS, ARUDHA PADAS & SHANNADI NAKSHATRA MATRIX'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi ? 'मांदि, गुलिक आदि अप्रकाश ग्रह, माया पद (Arudha Lagna AL/UL) तथा संवेदनशील नक्षत्र।' : 'Shadow sub-planets, 12 Arudha Maya padas and the 7 vital Nakshatra trigger points.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Box A: Upagrahas */}
            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <div className={`p-1.5 font-bold text-xs uppercase border-b ${tableHeaderBg} ${headingPrimary}`}>
                {isHi ? 'सप्त उपग्रह स्पष्ट स्थिति' : '7 Kalas & Upagrahas'}
              </div>
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-gray-700 text-[11px]">
                    <th className="p-1">Upagraha</th>
                    <th className="p-1">House</th>
                    <th className="p-1">Degree</th>
                    <th className="p-1">Influence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {upagrahas.map((up, i) => (
                    <tr key={i} className={i % 2 === 0 ? tableRowOdd : tableRowEven}>
                      <td className="p-1 font-bold text-astro-primary">{up.name}</td>
                      <td className="p-1 font-mono">H{up.house}</td>
                      <td className="p-1 font-mono text-[11px]">{up.deg}</td>
                      <td className="p-1 text-[10px] text-gray-600 truncate max-w-[120px]">{up.nature}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Box B: 12 Arudha Padas */}
            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <div className={`p-1.5 font-bold text-xs uppercase border-b ${tableHeaderBg} ${headingPrimary}`}>
                {isHi ? 'द्वादश भाव आरूढ़ पद (Maya Manifestation)' : '12 Arudha Padas (A1 to A12)'}
              </div>
              <div className="p-2 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                {arudhaPadas.map((ap, i) => (
                  <div key={i} className="flex items-center justify-between border-b pb-0.5">
                    <span className="font-bold text-astro-primary">{ap.code}:</span>
                    <span className="font-semibold text-gray-800">{ap.sign} (H{ap.house})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Shannadi Sensitive Nakshatras */}
          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <div className={`p-1.5 font-bold text-xs uppercase border-b ${tableHeaderBg} ${headingPrimary}`}>
              {isHi ? 'षन्नाडी संवेदनशील नक्षत्र चक्र' : '7 Shannadi & Sensitive Transit Nakshatras'}
            </div>
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-700 text-[11px]">
                  <th className="p-1">Classification</th>
                  <th className="p-1">Nakshatra</th>
                  <th className="p-1">Core Karmic Impact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {shannadiNakshatras.map((sn, i) => (
                  <tr key={i} className={i % 2 === 0 ? tableRowOdd : tableRowEven}>
                    <td className="p-1 font-bold text-astro-primary">{sn.title}</td>
                    <td className="p-1 font-mono font-semibold">{sn.nak}</td>
                    <td className="p-1 text-[11px] text-gray-700">{sn.impact}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ १ • पृष्ठ ५' : 'Grantha 1 • Page 5')}
      </div>
    </>
  );
};
